/**
 * A lista editável: linhas que o time acrescenta, escreve, reordena e apaga.
 *
 * <p>É o que a tela de Combate usa para as etapas, as ações e as distâncias. Nenhuma das
 * três tem número fechado nem forma fixa — são justamente o que está sendo desenhado —,
 * então a tela oferece a folha, e não um formulário do que ela imagina que a regra seja.
 *
 * <p>As operações moram aqui, fora do componente, porque são o que dá para prender em
 * teste: reordenar é onde erro de índice se esconde, e um erro desses só aparece quando
 * alguém já escreveu doze linhas.
 */

export interface Coluna {
  id: string;
  rotulo: string;
  /** O que se espera no campo — vai no `placeholder`, nunca no valor. */
  dica: string;
  /** Coluna larga ocupa a linha inteira em vez de dividir espaço. */
  larga?: boolean;
}

export interface Linha {
  /** Identidade estável: sobrevive à reordenação, ao contrário do índice. */
  id: string;
  valores: Record<string, string>;
}

/**
 * O próximo id da lista.
 *
 * <p>Sai do maior já usado, e não da contagem: apagar a linha 2 de três linhas deixaria
 * a contagem em 2 e o próximo id colidiria com a linha 3 que ficou. Chave repetida no
 * React faz duas linhas compartilharem estado — o campo de uma aparece na outra.
 */
export function proximoId(linhas: Linha[]): string {
  const maior = linhas.reduce((max, l) => {
    const n = Number(l.id.replace(/\D/g, ''));
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `l${maior + 1}`;
}

/**
 * Só o `id` das colunas entra aqui — daí o parâmetro pedir o mínimo que a função usa.
 *
 * <p>Não é generalização por gosto: a tabela de "Cenas, Ações e Fluxo" tem coluna
 * criada pelo time, sem a `dica` que a tela de Combate escreve em código. Exigir a
 * `Coluna` inteira obrigaria aquela tela a inventar uma dica vazia — ou a copiar esta
 * função, que é o que faria a álgebra de linha existir em dois lugares.
 */
type ComId = { id: string };

export const linhaEmBranco = (linhas: Linha[], colunas: readonly ComId[]): Linha => ({
  id: proximoId(linhas),
  valores: Object.fromEntries(colunas.map((c) => [c.id, ''])),
});

export const acrescentar = (linhas: Linha[], colunas: readonly ComId[]): Linha[] => [
  ...linhas,
  linhaEmBranco(linhas, colunas),
];

export const remover = (linhas: Linha[], id: string): Linha[] =>
  linhas.filter((l) => l.id !== id);

export const escrever = (
  linhas: Linha[],
  id: string,
  coluna: string,
  valor: string
): Linha[] =>
  linhas.map((l) => (l.id === id ? { ...l, valores: { ...l.valores, [coluna]: valor } } : l));

/**
 * Troca a linha de lugar com a vizinha.
 *
 * <p>Nas pontas não faz nada, em vez de dar a volta: a lista de etapas é uma ordem, e a
 * primeira etapa saltar para o fim ao clicar em "subir" seria o oposto do que o botão
 * promete. É também por isso que a tela desabilita a seta na ponta — o botão que não faz
 * nada precisa <i>parecer</i> que não faz.
 */
export function mover(linhas: Linha[], id: string, passo: -1 | 1): Linha[] {
  const i = linhas.findIndex((l) => l.id === id);
  const j = i + passo;
  if (i < 0 || j < 0 || j >= linhas.length) return linhas;
  const copia = [...linhas];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
}

/** Uma linha em que nada foi escrito. É o que a contagem de "escritas" ignora. */
export const vazia = (linha: Linha) => Object.values(linha.valores).every((v) => !v.trim());

export const escritas = (linhas: Linha[]) => linhas.filter((l) => !vazia(l)).length;

/**
 * O rascunho que ficou no `localStorage`, para a migração de uma vez só.
 *
 * <p>Não é mais a fonte: as listas do Combate moram no GG Pilot. Sobrevive como ponte,
 * para quem já tinha escrito no navegador encontrar tudo gravado na primeira abertura.
 */
export function lerLinhas(chave: string, colunas: Coluna[]): Linha[] {
  try {
    const cru = localStorage.getItem(chave);
    return interpretarLinhas(cru ? JSON.parse(cru) : null, colunas);
  } catch {
    return [];
  }
}

/**
 * As linhas a partir do que estava gravado na página.
 *
 * <p>Mesmo cuidado de `lerLinhas` — e pela mesma razão, só que a desconfiança agora é de
 * outra origem: o documento vem do GG Pilot, e foi escrito por uma versão anterior desta
 * tela. Uma linha sem `id`, ou um valor que não é texto, derruba a página no render.
 */
export function interpretarLinhas(guardado: unknown, colunas: Coluna[]): Linha[] {
  try {
    if (!Array.isArray(guardado)) return [];
    return guardado
      .filter((l): l is Linha => Boolean(l) && typeof l.id === 'string')
      .map((l) => ({
        id: l.id,
        valores: Object.fromEntries(
          colunas.map((c) => [c.id, typeof l.valores?.[c.id] === 'string' ? l.valores[c.id] : ''])
        ),
      }));
  } catch {
    return [];
  }
}

